import axios from '@/_utils/axios';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectCanWriteOrders } from '@/redux/selectors/authSelectors';
import { selectCurrentTableKey } from '@/redux/selectors/dataTableSelectors';
import { editOrder, getData } from '@/redux/slices/dataTableSlice';
import { AssemblyTemplate } from '@/redux/slices/packagingSlice.types';
import { JobDetail, OrderDetail, OrderJobAsset, OrderJobTask } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { ClickAwayListener, TextField } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PolarisIcon from './PolarisIcon';
import { useToast } from './Toast';

type EditableTextFieldProps = {
  value: string;
  label?: string;
  field: string;
  viewOnly?: boolean;
  emptyText?: string;
  required?: boolean;
  alwaysShowEditIcon?: boolean;
  validation?: { regex: RegExp; message: string };
} & (
  | {
      update: 'order';
      row: OrderDetail;
    }
  | {
      update: 'job';
      row: JobDetail;
    }
  | {
      update: 'task';
      row: OrderJobTask;
    }
  | {
      update: 'asset';
      row: OrderJobAsset;
    }
  | {
      update: 'assemblyTemplate';
      row: AssemblyTemplate;
    }
);

// translate the field into a nested object
const translateField = (field: string, value: string) => {
  const keys = field.split('__').reverse();
  return keys.reduce((obj: any, key: any) => ({ [key]: obj }), value.trim());
};

const EditableTextCell = ({
  value = '',
  update,
  label,
  field,
  viewOnly = false,
  required = true,
  emptyText = '-',
  row,
  alwaysShowEditIcon = false,
  validation
}: EditableTextFieldProps) => {
  const [view, setView] = useState<'loading' | 'display' | 'edit'>('display');
  const currentTableKey = useAppSelector(selectCurrentTableKey);
  const canWriteOrders = useAppSelector(selectCanWriteOrders);
  const isViewOnly = (currentTableKey === 'orders' && !canWriteOrders) || viewOnly || ('status' in row && row.status === 'draft');
  const nonNullValue = value ?? '';
  const [newText, setNewText] = useState<string>(nonNullValue);
  const inputLabel = label ? label : field.charAt(0).toUpperCase() + field.slice(1);
  const textRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const { success, warn, error } = useToast();
  const [valueOverride, setValueOverride] = useState<string | null>(null);

  const disableSaveButton = useMemo(() => {
    return required && !newText;
  }, [required, newText]);

  useEffect(() => {
    setNewText(nonNullValue);
  }, [value]);

  useEffect(() => {
    if (view === 'edit') {
      textRef.current.focus();
    } else if (view === 'display') {
      setNewText(nonNullValue);
    }
  }, [view]);

  const onKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      validate();
    } else if (e.key === 'Escape') {
      setView('display');
    }
  };

  const validate = () => {
    if (validation) {
      const test = validation.regex.test(newText);
      if (!test) {
        error(validation.message);
        return false;
      }
    }
    updateFunctions[update]();
  };

  const updateFunctions = {
    order: () => {
      setView('loading');
      (
        dispatch(
          editOrder({
            id: +row.id,
            description: (row as OrderDetail).description,
            po_number: (row as OrderDetail).po_number,
            [field]: newText
          })
        ) as any
      )
        .unwrap()
        .then(() => success(`${inputLabel} updated`))
        .catch((err: any) => error(err.message))
        .finally(() => setView('display'));
    },
    job: async () => {
      setView('loading');
      return await axios
        .post(`/core/jobs/${(row as JobDetail).id}/`, translateField(field, newText))
        .then((res) => {
          if (res?.data?.status || res?.data?.success) {
            success(`${inputLabel} updated`);
            setValueOverride(newText);
          } else {
            warn(`${inputLabel} not updated`);
          }
        })
        .catch((err) => {
          error(err.message);
          return err;
        })
        .finally(() => setView('display'));
    },
    task: async () => {
      setView('loading');
      return await axios
        .post(`/core/tasks/${(row as OrderJobTask).id}/`, translateField(field, newText))
        .then((res) => {
          if (res?.data?.status) {
            success(`${inputLabel} updated`);
          } else {
            warn(`${inputLabel} not updated`);
          }
        })
        .catch((err) => {
          error(err.message);
          return err;
        })
        .finally(() => setView('display'));
    },
    asset: async () => {
      setView('loading');
      return await axios
        .post(`/core/jobs/assets/${(row as OrderJobAsset).id}/`, translateField(field, newText))
        .then((res) => {
          if (res?.data?.success) {
            success(`${inputLabel} updated`);
            dispatch(getData());
          } else {
            warn(`${inputLabel} not updated`);
          }
        })
        .catch((err) => {
          error(err.message);
          return err;
        })
        .finally(() => setView('display'));
    },
    assemblyTemplate: async () => {
      setView('loading');
      return await axios
        .post(`/assembly_templates/${(row as AssemblyTemplate).id}/`, {
          ...row,
          [field]: newText
        })
        .then((res) => {
          if (res?.data?.success) {
            success(`${inputLabel} updated`);
            dispatch(getData());
          } else {
            warn(`${inputLabel} not updated`);
          }
        })
        .catch((err) => {
          error(err.message);
          return err;
        })
        .finally(() => setView('display'));
    }
  };

  const onEdit = (e: React.MouseEvent) => {
    if (isViewOnly) return;
    e.stopPropagation();
    setView('edit');
  };

  const onInputValueChanged = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setNewText(evt.target.value);
  };

  const renderView = () => {
    switch (view) {
      case 'display':
        return (
          <Display hoverable={+!isViewOnly} alwaysShowEditIcon={+alwaysShowEditIcon}>
            <div>{valueOverride || value || emptyText}</div>
            <EditButton onClick={onEdit} aria-label="Edit" type="button">
              <PolarisIcon icon="edit" />
            </EditButton>
          </Display>
        );
      case 'edit':
        return (
          <ClickAwayListener onClickAway={() => setView('display')} mouseEvent="onMouseDown" touchEvent="onTouchStart">
            <Edit onClick={(e) => e.stopPropagation()}>
              <TextField
                inputRef={textRef}
                id="edit-field"
                label={inputLabel}
                type="text"
                variant="filled"
                InputLabelProps={{ shrink: true }}
                fullWidth
                required={required}
                onChange={onInputValueChanged}
                value={newText}
                onKeyDown={onKeyDown}
                sx={{ mt: 3, minWidth: 150 }}
                size="small"
              />
              <EditButtons>
                <SaveButton
                  disabled={disableSaveButton}
                  icon="complete"
                  height={13}
                  onClick={(e) => {
                    if (!disableSaveButton) {
                      e.stopPropagation();
                      validate();
                    }
                  }}
                />
                <CancelButton
                  icon="close_filled"
                  height={16}
                  onClick={(e) => {
                    e.stopPropagation();
                    setView('display');
                  }}
                />
              </EditButtons>
            </Edit>
          </ClickAwayListener>
        );
      case 'loading':
        return (
          <SpinnerSpan>
            <LoadingIcon icon="loading_dots" />
          </SpinnerSpan>
        );
    }
  };

  return <Wrapper view={view}>{renderView()}</Wrapper>;
};

export default EditableTextCell;

const Wrapper = styled.div<{ view: 'loading' | 'display' | 'edit' }>`
  width: ${({ view }) => (view === 'display' ? 'auto' : '100%')};
`;

const SaveButton = styled(PolarisIcon)<{ disabled?: boolean }>`
  color: ${({ theme }) => theme.palette.success.main};
  cursor: pointer;
  margin-top: 1.25px;

  &:hover {
    color: ${({ theme }) => theme.palette.success.light};
  }

  ${(props) =>
    props.disabled
      ? `
    cursor: not-allowed;
    opacity: 0.3;
  `
      : null};
`;

const CancelButton = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.error.main};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.palette.error.light};
  }
`;

const Edit = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${({ theme }) => theme.spacing(1)};
  position: relative;
`;

const EditButtons = styled.div`
  display: flex;
  flex-direction: row;
  position: absolute;
  top: ${({ theme }) => theme.spacing(0.75)};
  right: ${({ theme }) => theme.spacing(1)};
  column-gap: ${({ theme }) => theme.spacing(1)};
`;

const Display = styled.div<{ hoverable: number; alwaysShowEditIcon: number }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  cursor: default;

  svg {
    display: none;
    color: ${({ theme }) => theme.palette.primary.main};
    margin-left: ${({ theme }) => theme.spacing(1)};
    height: 1.25rem;
    width: 1.25rem;
    min-width: 15px;
    min-height: 15px;
    cursor: pointer;
  }

  ${({ hoverable, alwaysShowEditIcon }) =>
    hoverable &&
    `
  svg {
    opacity: ${alwaysShowEditIcon ? 1 : 0};
    display: flex;
    transition: opacity 0.2s ease-in-out;
  }

  &:hover {
    svg {
      opacity: 1;
    }
  }
  
  `}
`;

const SpinnerSpan = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.palette.primary.main};
  margin-left: ${({ theme }) => theme.spacing(4)};
`;

const LoadingIcon = styled(PolarisIcon)`
  height: 2rem;
  width: 2rem;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: inherit;
  display: inline-flex;
  align-items: center;
  &:focus-visible {
    outline: 2px solid currentColor;
    border-radius: 4px;
  }
`;
